// ============================================================================
// psyReports.ts — ОТЧЁТЫ И ПЕЧАТЬ ПО МЕТОДИКАМ (модуль hydra.medic.psy).
//
// SSoT — сервер: отчёты конструктора (ReportService), печатные формы объекта
// (metadata_layouts, is_print) и протоколы прохождения (физтаблицы типов опросов).
// Здесь только чтение существующими RPC — новых серверных механизмов нет:
//   • каталог отчётов: имя отчёта = синониму (или имени) методики — тот же
//     детерминированный ключ, что у поставки psy (см. lib/polls.findMethodologyReportId);
//   • печатная форма методики: макет объекта parent_code = код типа опроса,
//     is_print = true («Заключение по тесту»);
//   • протоколы методики: строки физтаблицы {code} (дата/пациент/балл/заключение);
//   • отборы периода/пациента — отдельный модуль lib/psyReportsFilter.ts.
//
// Ошибки не глотаются: каталог без прав отдаёт denied=true (честное ограничение),
// недоступность печатных форм — warning, исчезнувшая физтаблица — tableMissing.
// ============================================================================

import { getSDK } from '@/store/sdkStore';
import { useCenterStore } from '@/store/centerStore';
import { useAuthStore } from '@/store/authStore';
import { readThrough } from '@/lib/offlineCache';
import { isNotFoundError, isPermissionDeniedError, logSwallowed } from '@/lib/errors';
import { ReportFormat } from '@/api/hooks/useReports';
import { printFormatFor } from '@/lib/printForm';
import { fileBase64 } from '@/lib/fileBytes';

/** Карточка отчёта методики (может быть без отчёта — только печать). */
export interface PsyReportEntry {
  /** Стабильный ключ списка (report:<id> | method:<code>). */
  key: string;
  methodCode: string;
  /** Синоним методики (он же имя отчёта в поставке psy). */
  methodName: string;
  methodFullName: string;
  reportId: string;
  reportName: string;
  reportDescription: string;
  printFormId: string;
  printFormName: string;
  printFormType: string;
}

export interface PsyReportCatalog {
  items: PsyReportEntry[];
  warnings: string[];
  /** Отчёты закрыты правами роли (каталог не получен). */
  denied: boolean;
}

/** Пациент для отбора (ядро «ГидраМедик» либо вертикаль psy). */
export interface PsyPatientOption {
  id: string;
  name: string;
}

/** Протокол прохождения (результат) методики. */
export interface PsyProtocolRecord {
  id: string;
  date: string;
  patientId: string;
  patientName: string;
  score: string;
  scaledScores: string;
  passed: boolean | null;
  conclusion: string;
}

export interface PsyProtocolsLoad {
  items: PsyProtocolRecord[];
  /** Права чтения протоколов не выданы. */
  denied: boolean;
  /** Физической таблицы типа нет (поставка не применена/тип удалён). */
  tableMissing: boolean;
}

export interface PsyReportRow {
  type: string;
  level: number;
  data?: Record<string, unknown>;
  aggregates?: Record<string, unknown>;
}

export interface PsyReportResult {
  columns: string[];
  rows: PsyReportRow[];
  totalRows: number;
}

export interface PsyPrintResult {
  fileName: string;
  mimeType: string;
  dataB64: string;
}

const PATIENT_CATALOGS = ['medic_patients', 'psy_clients'];

function slug(): string {
  return useCenterStore.getState().baseSlug || 'local';
}

/**
 * Ключ durable-кэша привязан к ПОЛЬЗОВАТЕЛЮ: иначе после смены учётной записи
 * на том же устройстве следующий пользователь увидел бы карточки пациентов
 * прошлого (данные персональные). Пустой id — отдельный ключ (аноним/офлайн).
 */
function viewer(): string {
  return useAuthStore.getState().user?.id || 'anonymous';
}

function norm(value: unknown): string {
  return String(value ?? '').trim().toLowerCase().replace(/ё/g, 'е');
}

/**
 * Имя отчёта методики — поставка psy использует синоним методики; допускаем
 * также префиксные варианты («Тесты: <методика>», «Отчёт: <методика>»), чтобы
 * каталог не зависел от косметики имени отчёта.
 */
function reportNameKey(value: unknown): string {
  return norm(value).replace(/^(тесты|отчет|отчёт)\s*:\s*/, '');
}

function reportMatches(reportName: unknown, type: { name: string; synonym: string }): boolean {
  const key = reportNameKey(reportName);
  if (key === '') return false;
  return key === norm(type.synonym) || key === norm(type.name);
}

function isTrue(value: unknown): boolean {
  const s = String(value ?? '').trim().toLowerCase();
  return s === 'true' || s === '1' || s === 'да';
}

function cellsOf(row: any): Record<string, string> {
  const out: Record<string, string> = {};
  for (const v of (row?.values || [])) out[String(v?.column || '')] = String(v?.value ?? '');
  return out;
}

// ---------------------------------------------------------------------------
// Каталог отчётов и печатных форм методик
// ---------------------------------------------------------------------------

async function fetchCatalog(): Promise<PsyReportCatalog> {
  const sdk = getSDK();
  const warnings: string[] = [];

  const typesRes: any = await sdk.poll.listPollTypes({});
  const types = ((typesRes?.items || []) as any[])
    .map((t) => ({
      code: String(t?.code || '').trim(),
      name: String(t?.name || '').trim(),
      synonym: String(t?.synonym || t?.name || t?.code || '').trim(),
    }))
    .filter((t) => t.code !== '');

  let reports: any[] = [];
  let denied = false;
  try {
    const reportsRes: any = await sdk.report.listReports({ folderId: '' });
    reports = (reportsRes?.items || []) as any[];
  } catch (e) {
    if (isPermissionDeniedError(e)) denied = true;
    else throw e;
  }

  // ОДИН запрос: все печатные формы объектов (is_print) — фильтр по parent_code ниже.
  let layouts: any[] = [];
  try {
    const layoutsRes: any = await sdk.metadata.listLayouts({ parentType: 'object', isPrint: true });
    layouts = (layoutsRes?.items || []) as any[];
  } catch (e) {
    logSwallowed(e, 'psyReports: печатные формы');
    warnings.push('печатные формы недоступны');
  }

  if (denied) throw new CatalogDeniedError({ items: [], warnings, denied: true });

  const usedReports = new Set<string>();
  const items: PsyReportEntry[] = [];

  for (const type of types) {
    const available = (r: any): boolean => {
      const id = String(r?.id || '');
      return id !== '' && !usedReports.has(id);
    };
    // 1) точное совпадение (синоним/имя, в т.ч. с префиксом «Тесты:»);
    // 2) осторожный запасной вариант — имя содержит синоним методики.
    const report = reports.find((r) => available(r) && reportMatches(r?.name, type))
      || reports.find((r) => {
        if (!available(r)) return false;
        const key = norm(r?.name);
        const synonym = norm(type.synonym);
        return synonym.length >= 4 && key.includes(synonym);
      });

    const forms = layouts.filter((l) => norm(l?.parentCode) === norm(type.code));
    const print = forms.find((l) => norm(l?.synonym).includes('заключен') || norm(l?.name).includes('заключен'))
      || forms[0];

    if (!report && !print) continue;
    if (report) usedReports.add(String(report.id || ''));

    items.push({
      key: report ? `report:${String(report.id)}` : `method:${type.code}`,
      methodCode: type.code,
      methodName: type.synonym || type.name || type.code,
      methodFullName: type.name,
      reportId: String(report?.id || ''),
      reportName: String(report?.name || ''),
      reportDescription: String(report?.description || ''),
      printFormId: String(print?.id || ''),
      printFormName: String(print?.synonym || print?.name || ''),
      printFormType: String(print?.layoutType || ''),
    });
  }

  // Отчёты без типа опроса (имя не совпало) — доступны только для просмотра.
  for (const report of reports) {
    const id = String(report?.id || '');
    if (!id || usedReports.has(id)) continue;
    const name = String(report?.name || '').trim();
    if (!name) continue;
    items.push({
      key: `report:${id}`,
      methodCode: '',
      methodName: name,
      methodFullName: '',
      reportId: id,
      reportName: name,
      reportDescription: String(report?.description || ''),
      printFormId: '',
      printFormName: '',
      printFormType: '',
    });
  }

  items.sort((a, b) => {
    if (!!a.methodCode !== !!b.methodCode) return a.methodCode ? -1 : 1;
    return a.methodName.localeCompare(b.methodName, 'ru');
  });

  return { items, warnings, denied };
}

/**
 * Отказ в правах не кэшируется: иначе следующий пользователь с правами увидел
 * бы «нет доступа» из durable-кэша. Сигнал проходит мимо кэша отдельным типом.
 */
class CatalogDeniedError extends Error {
  readonly catalog: PsyReportCatalog;
  constructor(catalog: PsyReportCatalog) {
    super('нет доступа к отчётам');
    this.catalog = catalog;
  }
}

/** Каталог отчётов по методикам (durable-кэш чтения — офлайн-фёрст). */
export async function loadPsyReportCatalog(): Promise<PsyReportCatalog> {
  try {
    const res = await readThrough<PsyReportCatalog>(`psy:report-catalog:${slug()}:${viewer()}`, fetchCatalog);
    return res.data;
  } catch (e) {
    if (e instanceof CatalogDeniedError) return e.catalog;
    throw e;
  }
}

// ---------------------------------------------------------------------------
// Пациенты (отбор)
// ---------------------------------------------------------------------------

async function fetchPatients(): Promise<PsyPatientOption[]> {
  const sdk = getSDK();
  const cats: any = await sdk.metadata.listCatalogs({});
  const codes = ((cats?.items || []) as any[]).map((c) => String(c?.code || ''));
  // ГидраМедик — medic_patients, вертикаль psy — psy_clients. Если есть оба,
  // объединяем: протоколы разных поставок ссылаются на свой справочник.
  const tables = PATIENT_CATALOGS.filter((code) => codes.includes(code));
  if (tables.length === 0) return [];

  const byId = new Map<string, PsyPatientOption>();
  for (const table of tables) {
    const r: any = await sdk.metadata.listTableData({ schema: 'public', table, pageSize: 500, page: 0 });
    for (const row of ((r?.rows || []) as any[])) {
      const id = String(row?.id || '');
      if (!id || byId.has(id)) continue;
      const cells = cellsOf(row);
      const name = String(cells.name || cells.fio || cells.full_name || '').trim();
      byId.set(id, { id, name: name || 'Пациент' });
    }
  }
  const out = [...byId.values()];
  out.sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  return out;
}

/** Пациенты для отбора (нет справочника — пустой список, без ошибки). */
export async function loadPsyPatients(): Promise<PsyPatientOption[]> {
  const res = await readThrough<PsyPatientOption[]>(`psy:report-patients:${slug()}:${viewer()}`, fetchPatients);
  return res.data;
}

// ---------------------------------------------------------------------------
// Протоколы методики
// ---------------------------------------------------------------------------

/** Протоколы методики за всё время (отбор периода/пациента — psyReportsFilter). */
export async function loadPsyProtocols(methodCode: string): Promise<PsyProtocolsLoad> {
  const empty: PsyProtocolsLoad = { items: [], denied: false, tableMissing: false };
  const code = String(methodCode || '').trim();
  if (!code) return empty;
  try {
    const r: any = await getSDK().metadata.listTableData({ schema: 'public', table: code, pageSize: 500, page: 0 });
    const items = ((r?.rows || []) as any[]).map((row): PsyProtocolRecord => {
      const cells = cellsOf(row);
      const passed = cells.passed === undefined || cells.passed === '' ? null : isTrue(cells.passed);
      return {
        id: String(row?.id || ''),
        date: String(cells.created_at || cells.result_date || ''),
        patientId: String(cells.subject_id || cells.patient_id || ''),
        patientName: String(cells.subject_id_presentation || cells.patient_id_presentation || ''),
        score: String(cells.score || cells.raw_scores || ''),
        scaledScores: String(cells.scaled_scores || ''),
        passed,
        conclusion: String(cells.conclusion || cells.result_interpretation || ''),
      };
    }).filter((r) => r.id !== '');
    items.sort((a, b) => b.date.localeCompare(a.date));
    return { items, denied: false, tableMissing: false };
  } catch (e) {
    if (isPermissionDeniedError(e)) return { items: [], denied: true, tableMissing: false };
    if (isNotFoundError(e)) return { items: [], denied: false, tableMissing: true };
    throw e;
  }
}

// ---------------------------------------------------------------------------
// Отчёт методики (серверный рендер)
// ---------------------------------------------------------------------------

/** Сформировать отчёт (JSON) — серверный движок отчётов, без параметров. */
export async function loadPsyReportResult(reportId: string): Promise<PsyReportResult> {
  const res: any = await getSDK().report.renderReport({
    id: reportId,
    parametersJson: '',
    format: ReportFormat.JSON,
    page: 0,
    pageSize: 2000,
  } as any);
  if (!res?.ok) throw new Error(String(res?.errorMessage || 'Отчёт не сформирован'));
  const parsed = JSON.parse(String(res?.dataJson || '{}'));
  const rows = Array.isArray(parsed?.rows) ? (parsed.rows as PsyReportRow[]) : [];
  return {
    columns: Array.isArray(parsed?.columns) ? parsed.columns.map((c: unknown) => String(c)) : [],
    rows,
    totalRows: Number(res?.totalRows || rows.length || 0),
  };
}

// ---------------------------------------------------------------------------
// Печать «Заключение по тесту» (рендер по записи протокола, SSoT — сервер)
// ---------------------------------------------------------------------------

/** Сформировать печатную форму ПО ЗАПИСИ протокола (pdf/html/docx/…). */
export async function renderPsyPrintForm(
  layoutId: string,
  recordId: string,
  layoutType: string,
): Promise<PsyPrintResult> {
  if (!layoutId) throw new Error('у методики нет печатной формы');
  if (!recordId) throw new Error('не выбрана запись протокола');
  const format = printFormatFor(layoutType); // явная ошибка для неподдерживаемого типа
  const r: any = await getSDK().metadata.renderLayout({
    layoutId,
    params: {},
    format,
    recordId,
  });
  return {
    fileName: String(r?.fileName || `Заключение по тесту.${format}`),
    mimeType: String(r?.mimeType || 'application/octet-stream'),
    dataB64: fileBase64(r?.data),
  };
}
